import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Plane, Globe2, Lock } from "lucide-react";
import api, { resolveImage } from "@/lib/api";
import { MapContainer, TileLayer, CircleMarker, Popup } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import Seo from "@/components/Seo";
import ShareButton from "@/components/ShareButton";
import { useAuth } from "@/contexts/AuthContext";

const TILE_URL = "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png";
const TILE_ATTRIB = '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';

const PinPopup = ({ pin }) => (
  <div className="min-w-[200px]">
    {pin.photo && (
      <img
        src={resolveImage(pin.photo)}
        alt={pin.title}
        className="w-full h-32 object-cover mb-2"
      />
    )}
    <p className="font-serif-display text-base text-ink leading-tight">
      {pin.title}
    </p>
    {pin.region && pin.region !== pin.title && (
      <p className="text-[11px] uppercase tracking-[0.18em] text-ink-soft mt-1">
        {pin.region}
      </p>
    )}
    {pin.date_label && (
      <p className="text-xs text-terracotta italic mt-2">
        {pin.status === "on_the_way" ? "✈️ " : "📍 "}{pin.date_label}
      </p>
    )}
    {pin.story && (
      <p className="text-sm text-ink-soft mt-2 leading-relaxed italic">
        {pin.story}
      </p>
    )}
  </div>
);

const JourneyMap = ({ pins, center, zoom, minZoom, kind, testId }) => {
  if (!pins.length) {
    return (
      <div
        data-testid={`${testId}-empty`}
        className="h-[340px] md:h-[420px] border border-rule bg-paper-2/40 flex items-center justify-center"
      >
        <p className="text-ink-soft italic font-serif-display text-xl">
          The first pin is on its way.
        </p>
      </div>
    );
  }
  return (
    <div
      data-testid={testId}
      className="h-[340px] md:h-[420px] border border-rule overflow-hidden bg-paper-2/40"
    >
      <MapContainer
        center={center}
        zoom={zoom}
        minZoom={minZoom}
        scrollWheelZoom={false}
        style={{ height: "100%", width: "100%" }}
      >
        <TileLayer attribution={TILE_ATTRIB} url={TILE_URL} />
        {pins.map((p) => (
          <CircleMarker
            key={p.id}
            center={[p.lat, p.lng]}
            radius={p.status === "on_the_way" ? 8 : 10}
            pathOptions={{
              color: p.status === "on_the_way" ? "#7C8D8E" : "#B5654A",
              fillColor: p.status === "on_the_way" ? "#A8B5B6" : "#C97A5E",
              fillOpacity: 0.85,
              weight: 2,
              dashArray: p.status === "on_the_way" ? "3,4" : null,
            }}
          >
            <Popup>
              <PinPopup pin={p} />
            </Popup>
          </CircleMarker>
        ))}
      </MapContainer>
    </div>
  );
};

const LegendDots = () => (
  <div className="flex flex-wrap gap-6 mt-4 text-xs text-ink-soft">
    <span className="inline-flex items-center gap-2">
      <span className="w-3 h-3 rounded-full bg-terracotta inline-block" /> Arrived
    </span>
    <span className="inline-flex items-center gap-2">
      <span className="w-3 h-3 rounded-full bg-sage/70 border border-dashed border-sage inline-block" /> On the way
    </span>
  </div>
);

export default function LucysJourney() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Wait until auth has resolved (user is null while loading; becomes object or false).
    if (user === null) return;
    setLoading(true);
    (async () => {
      try {
        if (user) {
          const [settingsRes, pinsRes] = await Promise.all([
            api.get("/admin/journey/settings"),
            api.get("/admin/journey/pins"),
          ]);
          setData({
            is_published: !!settingsRes.data.is_published,
            settings: settingsRes.data,
            pins: (pinsRes.data || []).filter((p) => p.is_visible),
            _adminPreview: true,
          });
        } else {
          const { data: res } = await api.get("/journey");
          setData(res);
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [user]);

  if (loading) {
    return (
      <div className="bg-paper min-h-[80vh] flex items-center justify-center">
        <p className="text-ink-soft">Loading…</p>
      </div>
    );
  }

  if (!data?.is_published && !data?._adminPreview) {
    return (
      <div className="bg-paper min-h-[80vh] flex items-center justify-center" data-testid="journey-not-published">
        <div className="max-w-md text-center px-6">
          <Plane size={28} className="mx-auto text-terracotta mb-4" />
          <h1 className="font-serif-display text-3xl text-ink mb-3">
            Lucy&apos;s journey is just beginning.
          </h1>
          <p className="text-ink-soft text-sm">
            Check back soon — soon, you&apos;ll be able to see where Lucy&apos;s boots have walked.
          </p>
        </div>
      </div>
    );
  }

  const settings = data.settings || {};
  const pins = data.pins || [];
  const worldPins = pins.filter((p) => p.kind === "world");
  const usaPins = pins.filter((p) => p.kind === "usa");
  const totalCities = pins.length;
  const totalCountries = new Set(
    pins.filter((p) => p.kind === "world").map((p) => p.region || p.title)
  ).size;
  const totalStates = new Set(
    pins.filter((p) => p.kind === "usa").map((p) => p.region || p.title)
  ).size;

  return (
    <div className="bg-paper" data-testid="lucys-journey-page">
      <Seo
        title={`${settings.heading || "Lucy's Journey"} — B. Ruff Writes`}
        description={settings.intro || "Where Lucy's boots have walked."}
      />
      <div className="max-w-5xl mx-auto px-6 lg:px-10 pt-10 lg:pt-14 pb-16">
        {!data.is_published && data._adminPreview && (
          <div
            data-testid="journey-draft-banner"
            className="border border-terracotta bg-terracotta/10 px-3 py-2 mb-6 text-xs text-terracotta flex items-center gap-2"
          >
            <Lock size={12} />
            <span>
              <strong>Draft mode</strong> — only you can see this page. Publish from Admin → Lucy&apos;s Journey when ready.
            </span>
          </div>
        )}

        <header className="border-b border-rule pb-6">
          <p className="text-xs uppercase tracking-[0.22em] text-terracotta mb-3 inline-flex items-center gap-2">
            <Globe2 size={12} /> The Journey
          </p>
          <h1 className="font-serif-display text-3xl lg:text-4xl text-ink tracking-tight leading-[1.1]">
            {settings.heading || "Lucy's Journey"}
          </h1>
          {settings.intro && (
            <p className="mt-3 text-sm text-ink-soft max-w-2xl leading-relaxed">
              {settings.intro}
            </p>
          )}

          <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-ink">
            <div className="flex items-center gap-1.5">
              <MapPin size={14} className="text-terracotta" />
              <span><strong className="font-serif-display text-lg">{totalCities}</strong> {totalCities === 1 ? "stop" : "stops"}</span>
            </div>
            <div className="flex items-center gap-1.5 text-ink-soft">
              <span><strong className="text-ink font-serif-display text-lg">{totalCountries}</strong> {totalCountries === 1 ? "country" : "countries"}</span>
            </div>
            <div className="flex items-center gap-1.5 text-ink-soft">
              <span><strong className="text-ink font-serif-display text-lg">{totalStates}</strong> US {totalStates === 1 ? "state" : "states"}</span>
            </div>
          </div>

          <div className="mt-4">
            <ShareButton
              title={`${settings.heading || "Lucy's Journey"} — track Lucy's book around the world`}
              excerpt={settings.intro}
              label="Share this page"
              className="inline-flex items-center gap-2 px-4 py-2 text-[11px] tracking-[0.18em] uppercase text-ink-soft hover:text-terracotta transition-colors"
            />
          </div>
        </header>

        {/* World section */}
        <section className="mt-8" data-testid="journey-world-section">
          <div className="border-b border-rule pb-3 mb-4">
            <p className="text-[11px] uppercase tracking-[0.22em] text-sage mb-2">🌍 Worldwide</p>
            <h2 className="font-serif-display text-2xl lg:text-3xl text-ink tracking-tight">
              {settings.world_heading || "Lucy Around the World"}
            </h2>
            {settings.world_intro && (
              <p className="mt-2 text-sm text-ink-soft max-w-2xl leading-relaxed italic">
                {settings.world_intro}
              </p>
            )}
          </div>
          <JourneyMap
            pins={worldPins}
            center={[20, 10]}
            zoom={2}
            minZoom={2}
            kind="world"
            testId="journey-world-map"
          />
          <LegendDots />
        </section>

        {/* USA section */}
        <section className="mt-10" data-testid="journey-usa-section">
          <div className="border-b border-rule pb-3 mb-4">
            <p className="text-[11px] uppercase tracking-[0.22em] text-sage mb-2">🇺🇸 United States</p>
            <h2 className="font-serif-display text-2xl lg:text-3xl text-ink tracking-tight">
              {settings.usa_heading || "Lucy Across the USA"}
            </h2>
            {settings.usa_intro && (
              <p className="mt-2 text-sm text-ink-soft max-w-2xl leading-relaxed italic">
                {settings.usa_intro}
              </p>
            )}
          </div>
          <JourneyMap
            pins={usaPins}
            center={[39.5, -98.35]}
            zoom={4}
            minZoom={3}
            kind="usa"
            testId="journey-usa-map"
          />
          <LegendDots />
        </section>

        <div className="mt-12 text-center text-xs text-ink-soft border-t border-rule pt-6">
          <p>
            Have a copy of Lucy in your home or town?{" "}
            <Link to="/about" className="text-terracotta link-underline">
              Let me know
            </Link>{" "}
            so I can add a pin. 💛
          </p>
        </div>
      </div>
    </div>
  );
}
